import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  WarningConfirmationModal,
} from "../../../../components";
import { MuiDateFieldFormController } from "../../../../components/dateField/MuiDateFieldFormController";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import {
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
} from "../../../../helpers/utils";
import { usePermissions } from "../../../../hooks/usePermissions";
import { editClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { PurchaseOrderKey } from "../../constants";
import { PurchaseOrder } from "../../modelsAndSchemas/Models";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { AddPurchaseOrder } from "../addPurchaseOrder/AddPurchaseOrder";
import PurchaseOrderManageTransaction from "../manageTransaction/PurchaseOrderManageTransaction";
import { ReOpenPurchaseOrder } from "../reOpenPurchaseOrder/ReOpenPurchaseOrder";
import { ViewPurchaseOrder } from "../viewPurchaseOrder/ViewPurchaseOrder";
import { PurchaseOrderAction } from "./PurchaseOrderAction";
import { PurchaseOrderListWrapper } from "./PurchaseOrderList.style";
import { usePurchaseOrderListHook } from "./usePurchaseOrderListHook";

const {
  poNumber,
  poEffectiveDate,
  poExpirationDate,
  poStatus,
  currentPoAmount,
  action,
  statusList,
  purchaseOrderTitle,
  addPurchaseOrderCta,
  deletePOActionText,
  noPoAvailable,
} = purchaseOrdersListSiteCoreKeys;

interface PurchaseOrderListProps {
  isAddButtonVisible?: boolean;
}

export const PurchaseOrderList: React.FC<PurchaseOrderListProps> = ({
  isAddButtonVisible = true,
}) => {
  const { isEdit: isEditClient } = useSelector(editClientSelector);
  const { hasModulePermission } = usePermissions();

  const {
    tableRef,
    pageId,
    filteredPoList,
    getLabel,
    commonLabels,
    handleAddPurchaseOrder,
    setShowAddPurchaseOrderModal,
    showAddPurchaseOrderModal,
    setShowManageTransactionModal,
    showManageTransactionModal,
    showDuplicateWarningModal,
    setShowDuplicateWarningModal,
    showViewPurchaseOrderModal,
    showViewPOtransactionsModal,
    showConfirmationModal,
    setShowConfirmationModal,
    setShowViewPurchaseOrderModal,
    setShowViewPOtransactionsModal,
    purchaseOrderList,
    purchaseOrder,
    handleActionClick,
    control,
    userCountryDateFormat,
    fetchDropdownValues,
    refetchPoList,
    handlePurchaseOrderEditFromView,
    purchaseOrderData,
    isEditPurchaseOrder,
    handlePurchaseOrderAdded,
    handleDelete,
    currencyList,
    commonARIALabel,
    showReOpenPoModal,
    setShowReOpenPoModal,
  } = usePurchaseOrderListHook();

  const columns: Column[] = [
    {
      id: PurchaseOrderKey.poNumber,
      label: getLabel(poNumber),
      isSortVisible: true,
      render: (row: RowProps) => {
        return hasModulePermission(
          MODULE_PERMISSIONS.VIEW_CLIENT_PURCHASE_ORDER,
        ) ? (
          <MuiLink
            onClick={() =>
              handleActionClick("viewPoTransactions", row as PurchaseOrder)
            }
          >
            <MuiBox>
              <MuiTypography variant="h3" className="Link">
                {row.poNumber}
              </MuiTypography>
            </MuiBox>
          </MuiLink>
        ) : (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {row.poNumber}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: PurchaseOrderKey.effectiveDate,
      label: getLabel(poEffectiveDate),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row.effectiveDate}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: PurchaseOrderKey.status,
      label: getLabel(poStatus),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge status={row.statusId} label={row.status} />
      ),
    },
    {
      id: PurchaseOrderKey.currentAmount,
      label: getLabel(currentPoAmount),
      isSortVisible: true,
      render: (row: RowProps) => {
        const currencyDetails = getCurrencyNameAndLocaleById(
          currencyList,
          row.currencyTypeId,
        );
        return `${currencyDetails.currencyName} ${getFormattedAmount(row.currentAmount, currencyDetails.currencyLocale)}`;
      },
    },
    {
      id: PurchaseOrderKey.expiryDate,
      label: getLabel(poExpirationDate),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row.expiryDate}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(action),
      render: (purchaseOrder: RowProps) => (
        <PurchaseOrderAction
          purchaseOrder={purchaseOrder as PurchaseOrder}
          isEditable={isAddButtonVisible || isEditClient}
          onActionCallback={(type) => {
            handleActionClick(type, purchaseOrder as PurchaseOrder);
          }}
        />
      ),
    },
  ];

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={PurchaseOrderKey.poNumber}
      key={PurchaseOrderKey.poNumber}
    >
      <MuiTextField
        id={`${pageId}-${PurchaseOrderKey.poNumber}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={PurchaseOrderKey.effectiveDate}
      key={PurchaseOrderKey.effectiveDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <FormController
      control={control}
      name={PurchaseOrderKey.status}
      key={PurchaseOrderKey.status}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(purchaseOrderTitle, statusList, true)}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${PurchaseOrderKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={PurchaseOrderKey.currentAmount}
      key={PurchaseOrderKey.currentAmount}
    >
      <MuiTextField
        id={`${pageId}-${PurchaseOrderKey.currentAmount}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      key={PurchaseOrderKey.expiryDate}
      control={control}
      clearable
      format={userCountryDateFormat}
      name={PurchaseOrderKey.expiryDate}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <></>,
  ];

  return (
    <PurchaseOrderListWrapper>
      {isAddButtonVisible &&
      hasModulePermission(MODULE_PERMISSIONS.ADD_CLIENT_PURCHASE_ORDER) ? (
        <MuiBox className="add-po-btn">
          <MuiButtonTertiary
            startIcon={<AddCircleOutlineIcon fontSize="large" />}
            onClick={handleAddPurchaseOrder}
          >
            {getLabel(addPurchaseOrderCta)}
          </MuiButtonTertiary>
        </MuiBox>
      ) : null}

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        isLastColumnSticky={isEditClient}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={filteredPoList}
        noDataInApi={purchaseOrderList.length === 0}
        emptyMessage={getLabel(noPoAvailable)}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
      />

      {showAddPurchaseOrderModal && (
        <AddPurchaseOrder
          open={showAddPurchaseOrderModal}
          onClose={() => setShowAddPurchaseOrderModal(false)}
          onPurchaseOrderAdded={handlePurchaseOrderAdded}
          isEdit={isEditPurchaseOrder}
          purchaseOrderData={purchaseOrderData}
        />
      )}

      {showViewPurchaseOrderModal && (
        <ViewPurchaseOrder
          open={showViewPurchaseOrderModal}
          onClose={() => setShowViewPurchaseOrderModal(false)}
          purchaseOrderData={purchaseOrderData}
          onPurchaseOrderEdit={handlePurchaseOrderEditFromView}
          isEditable={isAddButtonVisible || isEditClient}
        />
      )}

      {showReOpenPoModal && (
        <ReOpenPurchaseOrder
          open={showReOpenPoModal}
          onClose={() => setShowReOpenPoModal(false)}
          purchaseOrderData={purchaseOrderData}
          onPurchaseOrderAdded={handlePurchaseOrderAdded}
        />
      )}

      {showViewPOtransactionsModal && (
        <ViewPurchaseOrder
          open={showViewPOtransactionsModal}
          onClose={() => setShowViewPOtransactionsModal(false)}
          purchaseOrderData={purchaseOrderData}
          showTransactions={true}
          onPurchaseOrderEdit={handlePurchaseOrderEditFromView}
          isEditable={isAddButtonVisible || isEditClient}
        />
      )}

      {showConfirmationModal && (
        <WarningConfirmationModal
          labelContent={getLabel(deletePOActionText)}
          modalTitle={commonLabels.confirmation}
          show={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onPrimaryButtonClick={handleDelete}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      {showManageTransactionModal && (
        <PurchaseOrderManageTransaction
          open={showManageTransactionModal}
          purchaseOrderId={purchaseOrder?.purchaseOrderId ?? ""}
          purchaseOrderNumber={purchaseOrder?.poNumber ?? ""}
          currencyTypeId={purchaseOrder?.currencyTypeId ?? ""}
          currentPOBalance={Number(purchaseOrder?.currentAmount ?? 0)}
          onClose={() => setShowManageTransactionModal(false)}
          onPurchaseOrderUpdated={() => refetchPoList()}
        />
      )}
      {showDuplicateWarningModal && (
        <WarningConfirmationModal
          labelContent={getLabel(
            purchaseOrdersListSiteCoreKeys.purchaseOrderNumberInUseError,
          )}
          modalTitle={commonLabels.warning}
          show={showDuplicateWarningModal}
          onClose={setShowDuplicateWarningModal.bind(null, false)}
          onPrimaryButtonClick={() => setShowDuplicateWarningModal(false)}
          primaryButtonTitle={commonLabels.ok}
          secondaryButtonTitle={""}
        />
      )}
    </PurchaseOrderListWrapper>
  );
};
